// A contract's and a property's own page, in the words the records already use (BUGS.md T37, T45). The record a row
// of Contracts or Rights opens into printed every stored field under a label made from its code name, and an object
// as "key: value": "Matches video pa", "Days to end -857", "Document: signalId: <id>, subject: <name>". Each field
// here takes the words the page already uses for it elsewhere: the Contracts and Rights tables' column heads, the
// company record's agreement card (endWords, noticeWords, paperWords: records/account.ts), "Filed under" with the
// tier's name (labels.ts tierName, as Questions and the item page's Change say it) and "Offered by" (Add deal).
// A field nobody has worded yet keeps the label it had (fieldsOf), until its words are chosen.
import type { ContractRow, RightsRow } from '../api.js';
import { formatDate, tierName } from '../labels.js';
import { endWords, noticeWords, paperWords } from './account.js';

/** One line of the record: its label, its value, a note beside it, and the company it opens where it names one. */
export interface RecordField { label: string; value: string; note?: string; companyId?: string }

/** The record's own fields with labels made from their names: what a field nobody has worded yet still shows. */
export function fieldsOf(record: Record<string, unknown>): RecordField[] {
  const out: RecordField[] = [];
  for (const [k, v] of Object.entries(record)) {
    if (k === 'id' || k.endsWith('Id')) continue;
    const label = k.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, (c) => c.toUpperCase());
    let value: string;
    if (v === null || v === undefined) value = '';
    else if (Array.isArray(v)) value = v.map((x) => (typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))).join(', ');
    else if (typeof v === 'boolean') value = v ? 'yes' : 'no';
    else if (typeof v === 'object') value = Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== null).map(([a, b]) => `${a}: ${String(b)}`).join(', ');
    else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) value = formatDate(v);
    else value = String(v);
    out.push({ label, value });
  }
  return out;
}

const text = (v: string | number | null | undefined): string => (v === null || v === undefined ? '' : String(v));
const day = (v: string | null): string => (v ? formatDate(v) : '');
const yesNo = (v: boolean | null): string => (v === null ? '' : v ? 'yes' : 'no');

/** The fields not worded below, under the labels they had: the rest of the record, so nothing it holds is dropped. */
function rest(record: Record<string, unknown>, worded: readonly string[]): RecordField[] {
  return fieldsOf(Object.fromEntries(Object.entries(record).filter(([k]) => !worded.includes(k))));
}

const CONTRACT_WORDED = ['counterpartyName', 'clientAlias', 'igmDealId', 'product', 'competition', 'registerStatus', 'commencementDate', 'endDate', 'daysToEnd', 'initialTerm', 'autoRenew', 'noticePeriodDays', 'document', 'domainTier', 'paperUrl'] as const;

/** A contract's page, field by field, as the Contracts table heads and the agreement card word them. */
export function contractFields(c: ContractRow, now: Date = new Date()): RecordField[] {
  const end = endWords(c);
  // "62 days" as the Contracts table says it, within ninety days and not passed; the date alone says the rest.
  const days = c.endDate && c.daysToEnd !== null && c.daysToEnd >= 0 && c.daysToEnd <= 90 ? `${c.daysToEnd} days` : null;
  const notice = noticeWords({ noticeDays: c.noticePeriodDays, endDate: c.endDate }, now);
  const endNote = [days, end.note].filter((x): x is string => !!x).join(', ');
  const fields: RecordField[] = [
    { label: 'Counterparty', value: c.counterpartyName, companyId: c.counterpartyId, ...(c.clientAlias ? { note: c.clientAlias } : {}) },
    { label: 'IGM deal id', value: text(c.igmDealId) },
    { label: 'Product', value: text(c.product) },
    { label: 'Competition', value: text(c.competition) },
    { label: 'Status, as registered', value: text(c.registerStatus) },
    { label: 'Commenced', value: day(c.commencementDate) },
    { label: end.label, value: day(c.endDate), ...(endNote ? { note: endNote } : {}) },
    { label: 'Term', value: text(c.initialTerm) },
    { label: 'Auto-renew', value: yesNo(c.autoRenew) },
    { label: 'Notice', value: notice.value, ...(notice.sub ? { note: notice.sub } : {}) },
    // The signed paper in words, never its file name or its id (U324), as the agreement card names it.
    { label: 'Document', value: c.document ? (paperWords(c.document.subject ?? '') || 'signed document') : 'No document filed' },
    { label: 'Filed under', value: c.domainTier ? tierName(c.domainTier) : '' },
  ];
  return [...fields, ...rest(c as unknown as Record<string, unknown>, CONTRACT_WORDED)];
}

// `deals` and `operators` are the page's own links under the fields (shared.tsx RightLinks; BUGS.md D129), never a field.
const RIGHTS_WORDED = ['sport', 'isEsports', 'propertyName', 'rightsHolderName', 'rightsHolderOrgName', 'offeredBy', 'territory', 'territoryIso', 'domesticTier', 'matchesVideoPa', 'matchesDataPa', 'status', 'availableFrom', 'termExpiry', 'competition', 'deals', 'operators'] as const;

/** A property's page, field by field, as the Rights table heads word them. */
export function rightsFields(r: RightsRow): RecordField[] {
  const linked = r.competition ? text(r.competition.snowflakeCompetition ?? r.competition.oneplatformCompetitionId) : '';
  const usage: RecordField = r.competition
    ? { label: 'Usage data', value: r.competition.confirmed ? 'Linked' : 'Suggested', ...(linked ? { note: linked } : {}) }
    : { label: 'Usage data', value: 'Not linked' };
  const fields: RecordField[] = [
    { label: 'Sport', value: r.sport, ...(r.isEsports && r.sport.toLowerCase() !== 'esports' ? { note: 'e-sports' } : {}) },
    { label: 'Property', value: r.propertyName },
    r.rightsHolderOrgId && r.rightsHolderOrgName
      ? { label: 'Rights holder', value: r.rightsHolderOrgName, companyId: r.rightsHolderOrgId }
      : { label: 'Rights holder', value: text(r.rightsHolderName) },
    { label: 'Offered by', value: text(r.offeredBy) },
    { label: 'Territory', value: `${text(r.territory)}${r.territoryIso ? ` (${r.territoryIso})` : ''}` },
    { label: 'Tier', value: text(r.domesticTier) },
    { label: 'Video p.a.', value: text(r.matchesVideoPa) },
    { label: 'Data p.a.', value: text(r.matchesDataPa) },
    { label: 'Status', value: r.status },
    { label: 'From', value: day(r.availableFrom) },
    { label: 'Term / expiry', value: day(r.termExpiry) },
    usage,
  ];
  return [...fields, ...rest(r as unknown as Record<string, unknown>, RIGHTS_WORDED)];
}

/**
 * BUGS.md T37: where a contract's page opens its signed agreement, the address the company record's agreement card opens
 * (the API's paperUrl), and only when it is a web address; null, and the page draws no link.
 */
export function paperLink(page: string, record: Record<string, unknown>): string | null {
  if (page !== 'contracts') return null;
  const url = record['paperUrl'];
  return typeof url === 'string' && /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
}

/** The record's page: worded for a contract and a property, as it was for the rest. */
export function recordFields(page: string, record: Record<string, unknown>, now: Date = new Date()): RecordField[] {
  if (page === 'contracts') return contractFields(record as unknown as ContractRow, now);
  if (page === 'rights') return rightsFields(record as unknown as RightsRow);
  return fieldsOf(record);
}
